import { notify, useModal } from '@billy/design-system'
import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'

import { Contact } from '@features/contacts/types/contact'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCreateContact } from '@views/contacts/hooks/useCreateContact'

import { SUBMIT_SHORTCUT } from '../../../../constants/shortcuts'
import { ModalId } from '../../../../enums/modalId'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { useForm, useSegment } from '../../../../hooks'
import { ProcessingOverlay } from '../../../ProcessingOverlay'
import { ContactForm, contactFormDefaultValues, getValidationSchema } from '../utils/formData'
import { getContactFormDefaultValues } from '../utils/getContactFormDefaultValues'
import { getCreateContactPayload } from '../utils/getCreateContactPayload'

interface ContextState {
  isSaving: boolean
  submitForm: () => void
}

const ContactModalContext = createContext<ContextState | undefined>(undefined)

interface ContactModalContextProps {
  children: ReactNode
  defaultValues?: Partial<Contact>
  isSupplier: boolean
  onContactCreate?: (contact: Contact) => void
}

export const ContactModalContextProvider = ({
  children,
  defaultValues,
  isSupplier,
  onContactCreate,
}: ContactModalContextProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { track } = useSegment()
  const { close } = useModal(ModalId.CreateContactModal)
  const { create, isSaving } = useCreateContact({
    onSuccess: (data) => {
      close()
      track('Contact Created (Client)')
      onContactCreate?.(data.contacts[0])
    },
    onError: () => {
      notify({ id: NotificationKeys.CreateContact, message: t('error_saving'), variant: 'error' })
    },
  })

  const { Form, handleSubmit } = useForm({
    defaultValues: {
      ...contactFormDefaultValues,
      ...getContactFormDefaultValues(defaultValues),
    },
    validationSchema: useMemo(() => getValidationSchema(isSupplier, t), [isSupplier, t]),
  })

  const handleFormSubmit = useCallback(
    (values: ContactForm) => {
      const createContactPayload = getCreateContactPayload(values, isSupplier, organization)
      create(createContactPayload)
    },
    [isSupplier, organization, create],
  )

  const submitForm = useCallback(() => {
    handleSubmit(handleFormSubmit)()
  }, [handleSubmit, handleFormSubmit])

  // Shortcuts

  useHotkeys(SUBMIT_SHORTCUT, submitForm, { enableOnTags: ['INPUT', 'TEXTAREA', 'SELECT'] }, [])

  return (
    <ContactModalContext.Provider
      value={{
        isSaving,
        submitForm,
      }}
    >
      {isSaving && <ProcessingOverlay />}
      <Form>{children}</Form>
    </ContactModalContext.Provider>
  )
}

export const useContactModal = () => {
  const context = useContext(ContactModalContext)

  if (!context) {
    throw new Error('ContactModalContextProvider is missing in the module!')
  }

  return context
}
